import type { MatchingReadiness } from '../../../../services/api/matchingSessions';

/**
 * Shared by Start Matching and Set Up Matching, which both create a matching
 * session with the same endpoint and the same gating.
 */

/**
 * User-actionable error messages keyed by the medley-api `code` field
 * (set by `MatchingSessionController.createForCohort`'s error mapping).
 * Falls back to the apiClient-enhanced `message` if the code isn't mapped,
 * then to a generic retry message if neither is present.
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  COHORT_NO_INTAKE_FORM:
    'This cohort needs an intake form configured. Set one in cohort settings before starting matching.',
  NO_ELIGIBLE_PARTICIPANTS:
    'No participants have time slot availability set. Participants need to complete their intake before matching can start.',
  MATCHING_SERVER_AUTH_REJECTED:
    'Authentication with the matching service failed. Contact support.',
  MATCHING_SERVER_UNREACHABLE:
    "Couldn't reach the matching service. Try again in a moment, or contact support if it persists.",
  MATCHING_SERVER_INVALID_RESPONSE:
    'The matching service returned an unexpected response. Contact support.',
  MATCHING_SERVER_NOT_CONFIGURED:
    'The matching service is not configured for this environment. Contact support.',
};

export function deriveStartErrorMessage(err: unknown): string {
  const e = err as { code?: string; message?: string };
  if (e?.code && ERROR_CODE_MESSAGES[e.code])
    return ERROR_CODE_MESSAGES[e.code];
  if (e?.message) return e.message;
  return 'Try again or contact support if the problem persists.';
}

/**
 * Disabled when (a) cohort-level prereqs (session dates / time slots) aren't
 * met, (b) readiness is still loading, or (c) the backend reports blockers.
 */
export function isStartDisabled(
  groupsPrereqsMet: boolean,
  readiness: MatchingReadiness | undefined
): boolean {
  return !groupsPrereqsMet || readiness === undefined || !readiness.ready;
}

/** Tooltip lines explaining why a start button is disabled. */
export function startDisabledReasons(
  groupsPrereqsMet: boolean,
  readiness: MatchingReadiness | undefined
): string[] {
  const reasons: string[] = [];
  if (!groupsPrereqsMet) {
    reasons.push(
      'Cohort needs session dates and time slots configured (see the alert above the panel).'
    );
  }
  if (readiness === undefined) {
    reasons.push('Checking matching readiness…');
  } else if (!readiness.ready) {
    for (const blocker of readiness.blocking_reasons) {
      reasons.push(blocker.message);
    }
  }
  return reasons;
}
